import {
  DEFAULT_VIEWPORT,
  EMPTY_VIEWPORT_SIZE,
  type MarqueeState,
  type Store,
  type ViewportSize,
  type ViewportState,
  createLens,
  createStore,
  deriveStore,
} from '@frontend/viewer-core';

import { MAPPING_HISTORY_CAPACITY } from '../constants';
import { createAccentSourceSlice } from './accentBuilder';
import { createDrawnHiddenLayers, createDrawnOverlays } from './drawnDisplay';
import {
  entityFrameBounds,
  featureSnapDuration,
  featureViewport,
} from './entityFrame';
import {
  createHighlightController,
  createHighlightsSlice,
} from './highlightController';
import { createHistoryStore, type HistoryStore } from './historyStore';
import { createInterrogationLineBuilder } from './interrogationLines';
import {
  createHiddenLayersSlice,
  createHoveredLayerSlice,
  createLayerController,
} from './layerController';
import {
  createRoleBatchBuilder,
  createRoleOverlaySourceSlice,
} from './mappingAccents';
import {
  createHoveredRoleSlice,
  createMappingController,
  createMappingSlice,
  createMappingSnapshotSlice,
} from './mappingController';
import {
  assignmentsEqual,
  changedEntityKeys,
  entityKeyLayers,
  selectionLayers,
} from './mappingModel';
import { createMappingValidationController } from './mappingValidation';
import { createMarqueeController } from './marqueeController';
import { createMaterialFillBuilder } from './materialFill';
import {
  createMeasureController,
  createMeasureSlice,
} from './measureController';
import { createModeController, createModeSlice } from './modeController';
import {
  createOverlayController,
  createOverlaysSlice,
} from './overlayController';
import { createPointerTracker } from './pointerTracker';
import { createRendererBinding } from './rendererBinding';
import { createRoleChromeController } from './roleChromeController';
import {
  createModelStore,
  createSceneLoader,
  createStatusStore,
} from './sceneLoader';
import {
  createHoveredSlice,
  createSelectionController,
  createSelectionSlice,
} from './selectionController';
import {
  createSelectionGranularityController,
  createSelectionGranularitySlice,
} from './selectionGranularity';
import { createSnapResolver } from './snapResolver';
import { createSurfaceBinding } from './surfaceBinding';
import { createToolController, createToolSlice } from './toolController';
import {
  EMPTY_POINTER,
  INITIAL_LOAD_STATE,
  INITIAL_SESSION_STATE,
  NO_MEASURE_MODIFIERS,
  type DxfSceneLoadState,
  type DxfViewerSession,
  type MappingSnapshot,
  type MarqueePreview,
  type MeasureModifiers,
  type PointerState,
} from './types';
import { createViewportController } from './viewportController';

/**
 * Creates a DXF viewer session.
 *
 * One session per provider, created once and never replaced — its identity is what
 * makes `DxfViewerContext` inert. Nothing here is allowed to reach React unless a
 * person caused it: viewport and gesture state live in stores the session owns,
 * not in provider state, so panning or zooming never triggers a provider
 * re-render on its own.
 *
 * The session owns two stores with different audiences:
 *
 * - `state` — discrete, React-facing interaction state (selection, tools, …).
 * - `load` — drawing fetch / derive lifecycle (`idle` | `loading` | `ready` | `error`).
 * - `viewport` (and its `size`) — frame rate, subscribed to by the renderer and the
 *   text layer only.
 *
 * Keeping those apart is the whole point; putting the viewport in `state` would
 * restore the original problem exactly.
 *
 * This function is deliberately just wiring. `state` itself is never handed to a
 * controller — each field it holds is carved off with `createLens` into its own
 * `Store`, defined in and exported by whichever controller module owns that field
 * (`createMeasureSlice` next to `createMeasureController`, and so on), and only
 * that narrow slice is passed down. A controller that needs to *read* a field it
 * doesn't own (selection needing to know which layers are hidden, say) gets a bare
 * getter function instead of the slice itself, so it can't write what it doesn't
 * own. What's left here is creating the stores and slices, handing each controller
 * exactly what it declared it needs, and assembling the result.
 */
export const createDxfViewerSession = (): DxfViewerSession => {
  const state = createStore(INITIAL_SESSION_STATE);
  const loadStore: Store<DxfSceneLoadState> = createStore(INITIAL_LOAD_STATE);
  const modelStore = createModelStore(loadStore);
  const statusStore = createStatusStore(loadStore);
  const viewportStore: Store<ViewportState> = createStore(DEFAULT_VIEWPORT);
  const sizeStore: Store<ViewportSize> = createStore(EMPTY_VIEWPORT_SIZE);
  const marqueeStore: Store<MarqueeState | null> = createStore(null);
  const previewStore: Store<MarqueePreview | null> = createStore(null);
  const pointerStore: Store<PointerState> = createStore(EMPTY_POINTER);
  const measureModifiersStore: Store<MeasureModifiers> =
    createStore(NO_MEASURE_MODIFIERS);

  // One lens per field of `state`. Each is defined next to the controller that
  // owns it; `isPaused` has no controller of its own, so its lens lives here.
  const hiddenLayers = createHiddenLayersSlice(state);
  const hoveredLayer = createHoveredLayerSlice(state);
  const selectionSlice = createSelectionSlice(state);
  const hoveredSlice = createHoveredSlice(state);
  const measureSlice = createMeasureSlice(state);
  const overlaysSlice = createOverlaysSlice(state);
  const toolSlice = createToolSlice(state);
  const granularitySlice = createSelectionGranularitySlice(state);
  const modeSlice = createModeSlice(state);
  const mappingSlice = createMappingSlice(state);
  const hoveredRoleSlice = createHoveredRoleSlice(state);
  const highlightsSlice = createHighlightsSlice(state);
  // Selection and draft as one writable unit, and the undo stack over it. Created
  // before the controllers because `layers` needs its suppression scope.
  const mappingSnapshot = createMappingSnapshotSlice(state);
  const recordedHistory = createHistoryStore(mappingSnapshot, {
    capacity: MAPPING_HISTORY_CAPACITY,
  });
  const isPausedSlice = createLens(
    state,
    (current) => current.isPaused,
    (current, isPaused) => ({ ...current, isPaused }),
  );
  /** The part file this viewer shell is bound to — set by `DXFViewer` from its prop. */
  const partFileUuidStore = createStore<string | null>(null);
  const mappingValidation = createMappingValidationController();
  const accentSource = createAccentSourceSlice(state);
  const roleOverlaySource = createRoleOverlaySourceSlice(state);

  const getModel = modelStore.getState;
  // What is drawn and picked, as distinct from what the user chose — the two
  // differ only while viewing interrogation results. See `drawnDisplay`.
  const drawnHiddenLayers = createDrawnHiddenLayers(state);
  const drawnOverlays = createDrawnOverlays(state);

  const viewport = createViewportController({
    viewportStore,
    sizeStore,
    getModel,
    getHiddenLayers: drawnHiddenLayers.getState,
  });
  const layers = createLayerController({
    hiddenLayers,
    hoveredLayer,
    selection: selectionSlice,
    getModel,
    viewport,
    withoutRecording: recordedHistory.withoutRecording,
  });
  const mapping = createMappingController({
    mapping: mappingSlice,
    snapshot: mappingSnapshot,
    selection: selectionSlice,
    hoveredRole: hoveredRoleSlice,
    getModel,
    getHiddenLayers: drawnHiddenLayers.getState,
  });
  const selectionGranularity = createSelectionGranularityController({
    granularity: granularitySlice,
  });
  const selection = createSelectionController({
    selection: selectionSlice,
    hovered: hoveredSlice,
    getHiddenLayers: drawnHiddenLayers.getState,
    // The one wiring point for what a click picks: the *effective* granularity,
    // so Alt's inversion applies to every pick without any caller of `pickAt`
    // knowing it can be overridden.
    getGranularity: selectionGranularity.effective,
    // Read per pick, not captured: loop chaining stops at a role boundary, so a
    // brush stroke changes what the very next click selects — without the model
    // being re-derived or a single `loopId` moving.
    getAssignments: () => mapping.effective(),
    getModel,
    viewport,
  });
  const resolveSnap = createSnapResolver({
    viewport,
    getModel,
    getTool: toolSlice.getState,
    getHiddenLayers: drawnHiddenLayers.getState,
    getMeasureModifiers: measureModifiersStore.getState,
  });
  const pointerTracker = createPointerTracker({
    pointerStore,
    resolve: resolveSnap,
  });
  const measure = createMeasureController({
    measure: measureSlice,
    modifiers: measureModifiersStore,
    resolve: resolveSnap,
    getModel,
  });
  const overlays = createOverlayController({ overlays: overlaysSlice });
  const highlights = createHighlightController({ highlights: highlightsSlice });
  const marqueeController = createMarqueeController({
    marqueeStore,
    previewStore,
    model: getModel,
    getHiddenLayers: drawnHiddenLayers.getState,
    // The same two reads a click makes, so a box and a click agree on what a
    // chain is — including while Alt is inverting the granularity mid-drag.
    getAssignments: () => mapping.effective(),
    granularity: selectionGranularity.effective,
    selection,
    screenToWorld: viewport.screenToWorld,
  });
  const roleChrome = createRoleChromeController({
    mode: modeSlice,
    selection: selectionSlice,
  });
  const tools = createToolController({
    tool: toolSlice,
    selection,
    measure,
    marqueeController,
    pointerTracker,
    roleChrome,
  });
  /**
   * Shows any layer an undo or redo has just brought back into play.
   *
   * A step is only worth taking if its result is visible. Hiding a layer prunes the
   * live selection but leaves earlier steps holding it, so walking back to one can
   * restore a selection — or a role change — that sits on a layer since hidden.
   * Revealing it is the alternative to silently dropping the step or to pruning the
   * past, and it is the one that keeps undo honest: what changed is what you see.
   *
   * Deliberately one-way. Visibility is not part of a snapshot, so a layer is never
   * re-hidden by stepping the other way — that would turn undo into a second,
   * confusing control over what is on screen.
   */
  const revealAffectedLayers = (
    before: MappingSnapshot,
    after: MappingSnapshot,
  ) => {
    const model = getModel();
    if (!model) {
      return;
    }
    const affected = new Set([
      ...selectionLayers(after.selection, model),
      ...entityKeyLayers(changedEntityKeys(before.draft, after.draft), model),
    ]);
    affected.forEach((layerIndex) => layers.setVisible(layerIndex, true));
  };

  /** Wraps a history move so whatever it restored is on screen afterwards. */
  const withReveal = (move: () => void) => () => {
    const before = mappingSnapshot.getState();
    move();
    revealAffectedLayers(before, mappingSnapshot.getState());
  };

  const mappingHistory: HistoryStore = {
    ...recordedHistory,
    undo: withReveal(recordedHistory.undo),
    redo: withReveal(recordedHistory.redo),
  };

  const mode = createModeController({
    mode: modeSlice,
    tool: toolSlice,
    mapping,
    measure,
    overlays,
    marqueeController,
    pointerTracker,
    roleChrome,
    history: mappingHistory,
    highlights,
    getModel,
  });
  // Derived rather than stored: compares draft to the entry baseline by value so
  // a round-trip edit that lands on the same roles is not treated as unsaved.
  const hasUnsavedMapping = deriveStore(mappingSlice, (current) => {
    const { draft, baseline } = current;
    if (draft === null || baseline === null) {
      return false;
    }
    return !assignmentsEqual(draft, baseline);
  });
  const loadSceneInto = createSceneLoader({
    load: loadStore,
    viewport,
    layers,
    selection,
    selectionGranularity,
    measure,
    marqueeController,
    pointerTracker,
    mode,
    mapping,
    history: mappingHistory,
    tools,
  });
  const bindSurface = createSurfaceBinding({
    sizeStore,
    model: modelStore,
    getTool: toolSlice.getState,
    granularity: selectionGranularity,
    tools,
    mapping,
    roleChrome,
    getMode: modeSlice.getState,
    getIsPaused: isPausedSlice.getState,
    history: mappingHistory,
    viewport,
    selection,
    marqueeController,
    marqueePreview: {
      getState: previewStore.getState,
      subscribe: previewStore.subscribe,
    },
    measure,
    pointerTracker,
  });
  const buildRoleBatches = createRoleBatchBuilder();
  const buildMaterialFill = createMaterialFillBuilder();
  const buildInterrogationLines = createInterrogationLineBuilder();
  const bindRenderer = createRendererBinding({
    viewportStore,
    sizeStore,
    modelStore,
    hiddenLayers: drawnHiddenLayers,
    getSelection: selectionSlice.getState,
    getHovered: hoveredSlice.getState,
    getHoveredLayer: hoveredLayer.getState,
    getHoveredRole: hoveredRoleSlice.getState,
    getHighlights: highlightsSlice.getState,
    overlays: drawnOverlays,
    mode: modeSlice,
    accentSource,
    preview: previewStore,
    roleOverlaySource,
    getRoleIndex: mapping.roleIndex,
    getHasMapping: () => mapping.effective() !== null,
    buildRoleBatches,
    buildInterrogationLines,
    buildMaterialFill,
  });

  // Disposal state values
  let detachSurface: (() => void) | null = null;
  let isDisposed = false;

  return {
    state: { getState: state.getState, subscribe: state.subscribe },
    load: { getState: loadStore.getState, subscribe: loadStore.subscribe },
    status: {
      getState: statusStore.getState,
      subscribe: statusStore.subscribe,
    },
    viewport,
    model: { getState: modelStore.getState, subscribe: modelStore.subscribe },
    layers,
    selection,
    tools,
    selectionGranularity,
    mode,
    mapping,
    mappingHistory,
    hasUnsavedMapping,
    measure,
    overlays,
    highlights,
    roleChrome,
    marqueeController,
    marquee: {
      getState: marqueeStore.getState,
      subscribe: marqueeStore.subscribe,
    },
    marqueePreview: {
      getState: previewStore.getState,
      subscribe: previewStore.subscribe,
    },
    pointer: pointerTracker.store,
    hiddenLayers,
    drawnHiddenLayers,
    drawnOverlays,
    hoveredLayer,

    partFileUuid: partFileUuidStore,

    setPartFileUuid(partFileUuid) {
      partFileUuidStore.setState((previous) =>
        previous === partFileUuid ? previous : partFileUuid,
      );
    },

    mappingValidation,

    attachSurface(host) {
      detachSurface?.();
      const detach = bindSurface(host);
      detachSurface = () => {
        detach();
        detachSurface = null;
      };
      return detachSurface;
    },

    attachRenderer: bindRenderer,

    loadScene(input) {
      if (isDisposed) {
        return;
      }
      loadSceneInto(input);
    },

    setPaused(isPaused) {
      isPausedSlice.setState((previous) =>
        previous === isPaused ? previous : isPaused,
      );
    },

    frameEntities(keys, spans = []) {
      const model = getModel();
      const bounds = entityFrameBounds(model, keys, spans);
      if (!bounds) {
        return;
      }
      const current = viewport.getState();
      const size = viewport.size.getState();
      const target = featureViewport(
        bounds,
        model?.bounds ?? null,
        size,
        current.quarterTurns,
      );
      viewport.animateTo(target, featureSnapDuration(current, target, size));
    },

    dispose() {
      isDisposed = true;
      viewport.cancelAnimation();
      detachSurface?.();
      // Before the load reset, so the teardown does not get recorded as a step.
      mappingHistory.detach();
      loadStore.setState(() => INITIAL_LOAD_STATE);
    },
  };
};
